import { IComboBox, IComboBoxOption } from '@fluentui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { UserFilterOption, UserFiltersResult } from '../types';

/**
 * Custom hook to manage user filter state (date-range-scoped)
 *
 * Manages the selection state for user filters. Filter options are provided
 * from external source (usePrefetchedFilterOptions) which extracts them from
 * already-fetched user usage data.
 *
 * The hook maintains two states:
 * - pendingUsers: UI state that updates immediately on selection
 * - appliedUsers: Committed state that triggers data fetches
 *
 * IMPORTANT: When date range changes, both pending and applied filters are automatically reset.
 * This is because user filters are scoped to the current date range.
 *
 * @param userOptions Filter options extracted from user usage data
 * @param startTime Start of the current time range (for change detection)
 * @param endTime End of the current time range (for change detection)
 * @param granularity Granularity of the current time range (for change detection)
 * @returns Object containing user options, pending/applied users, handlers, and computed states
 */
export function useUserFilters(
  userOptions: UserFilterOption[],
  startTime: Date,
  endTime: Date,
  granularity: Granularity
): UserFiltersResult {
  // Pending state: immediate UI updates, no fetch triggered
  const [pendingUsers, setPendingUsers] = useState<string[]>([]);

  // Applied state: committed filters that trigger data fetches
  const [appliedUsers, setAppliedUsers] = useState<string[]>([]);

  // Track previous date range to detect changes
  const prevDateRangeRef = useRef<string>('');
  const currentDateRangeKey = `${startTime.toISOString()}-${endTime.toISOString()}-${granularity}`;

  // Auto-reset filters when date range changes
  useEffect(() => {
    if (prevDateRangeRef.current !== '' && prevDateRangeRef.current !== currentDateRangeKey) {
      setPendingUsers([]);
      setAppliedUsers([]);
    }
    prevDateRangeRef.current = currentDateRangeKey;
  }, [currentDateRangeKey]);

  // Handle user filter changes (updates pending state only, no fetch)
  const handleChange = useCallback(
    (_event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
      if (!option) return;

      setPendingUsers((prev) => {
        const updatedUsers = option.selected
          ? [...prev, option.key as string]
          : prev.filter((user) => user !== option.key);

        return updatedUsers;
      });
    },
    [] // No dependencies - stable callback reference
  );

  // Apply handler: commits pending changes to applied state (triggers fetch)
  const handleApply = useCallback(() => {
    setAppliedUsers(pendingUsers);
  }, [pendingUsers]);

  // Reset handler: implements two scenarios
  // 1. If no applied filters: clear pending only (no fetch)
  // 2. If applied filters exist: clear both pending and applied (triggers fetch)
  const handleReset = useCallback(() => {
    setPendingUsers([]);

    // Only clear applied filters if there were any (triggers fetch only if needed)
    if (appliedUsers.length > 0) {
      setAppliedUsers([]);
    }
  }, [appliedUsers]);

  // Computed values for button states
  const hasChanges = JSON.stringify([...pendingUsers].sort()) !== JSON.stringify([...appliedUsers].sort());
  const hasFiltersToReset = pendingUsers.length > 0 || appliedUsers.length > 0;

  return {
    userOptions,
    selectedUsers: pendingUsers,
    appliedUsers,
    handleChange,
    handleApply,
    handleReset,
    hasChanges,
    hasFiltersToReset,
  };
}
